/**
 * HR Engine - Page de Validation RH
 * Permet au RH de valider les évaluations après l'évaluation manager
 * Conforme au document EVALUATION DE PERFORMANCE- 2025
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Separator } from '../components/ui/separator';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import {
  ArrowLeft,
  Loader2,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  User,
  Building2,
  Calendar,
  Target,
  Star,
  TrendingUp,
  FileText,
  Download,
  Send,
  RotateCcw,
  Award,
  MessageSquare
} from 'lucide-react';

// Niveaux de performance
const PERFORMANCE_LEVELS = {
  high_performer: { label: 'High Performer', color: 'bg-emerald-100 text-emerald-700 border-emerald-300', description: 'Star confirmée - Mettre en place un système de motivation et de rétention' },
  performer: { label: 'Performer', color: 'bg-blue-100 text-blue-700 border-blue-300', description: 'Conforme aux attentes - Renforcer les acquis et travailler sur les points faibles' },
  non_performer: { label: 'Non Performer', color: 'bg-red-100 text-red-700 border-red-300', description: 'Non conforme aux attentes - Étudier l\'adéquation compétences/poste' }
};

const getPerformanceLevel = (score) => {
  if (score >= 85) return 'high_performer';
  if (score >= 60) return 'performer';
  return 'non_performer';
};

// Barème conforme au document client
const BAREME = [
  { min: 0, max: 49, note: '/20 < 10', status: 'Non conforme aux attentes', action: 'Étudier l\'adéquation compétences et exigences du poste' },
  { min: 50, max: 59, note: '/20 = 10-12', status: 'Objectif moyennement atteint', action: 'Formation et accompagnement du salarié' },
  { min: 60, max: 74, note: '/20 = 13-15', status: 'Conforme aux attentes', action: 'Renforcer les acquis et travailler sur les points faibles' },
  { min: 75, max: 84, note: '/20 = 16-18', status: 'Performant', action: 'Future star - Mettre en place un système de motivation et de rétention' },
  { min: 85, max: 100, note: '/20 = 18-20', status: 'Très performant', action: 'Star confirmée - Mettre en place un système de motivation et de rétention' }
];

const getBaremeInfo = (score) => {
  return BAREME.find(b => score >= b.min && score <= b.max) || BAREME[0];
};

export default function RHValidationPage() {
  const { evaluationId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [evaluation, setEvaluation] = useState(null);
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);

  // Form state
  const [validationForm, setValidationForm] = useState({
    approved: true,
    comment: '',
    adjustedScore: null,
    developmentNeeds: '',
    trainingRecommendations: ''
  });

  useEffect(() => {
    fetchEvaluation();
  }, [evaluationId]);

  const fetchEvaluation = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/performance-eval/evaluations/${evaluationId}`);
      setEvaluation(response.data);
      
      // Initialize adjusted score with manager score
      if (response.data.managerScore) {
        setValidationForm(prev => ({
          ...prev,
          adjustedScore: response.data.managerScore
        }));
      }
    } catch (error) {
      console.error('Error fetching evaluation:', error);
      toast.error('Erreur lors du chargement de l\'évaluation');
    } finally {
      setLoading(false);
    }
  };

  const handleValidate = async (approved) => {
    try {
      setSubmitting(true);
      
      await api.post(`/performance-eval/evaluations/${evaluationId}/validate`, {
        approved,
        comment: validationForm.comment,
        adjustedScore: approved ? validationForm.adjustedScore : null
      });

      if (approved) {
        toast.success('Évaluation validée avec succès !');
      } else {
        toast.info('Évaluation renvoyée au manager pour révision');
      }
      
      setShowApproveDialog(false);
      setShowRejectDialog(false);
      navigate('/evaluation-campaigns');

    } catch (error) {
      console.error('Error validating:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la validation');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await api.get(`/performance-eval/evaluations/${evaluationId}/pdf`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Evaluation_${evaluation?.employee?.firstName}_${evaluation?.employee?.lastName}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      
      toast.success('PDF téléchargé');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Erreur lors du téléchargement');
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Validation RH">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-webtech-blue" />
        </div>
      </DashboardLayout>
    );
  }

  if (!evaluation) {
    return (
      <DashboardLayout title="Validation RH">
        <Card className="py-12">
          <CardContent className="text-center">
            <AlertTriangle className="w-12 h-12 mx-auto text-amber-500 mb-4" />
            <h3 className="text-lg font-semibold mb-2">Évaluation non trouvée</h3>
            <Button onClick={() => navigate('/evaluation-campaigns')}>
              Retour aux campagnes
            </Button>
          </CardContent>
        </Card>
      </DashboardLayout>
    );
  }

  const emp = evaluation.employeeSnapshot || evaluation.employee || {};
  const managerEval = evaluation.managerEvaluation || {};
  const selfEval = evaluation.selfEvaluation || {};
  const objectives = evaluation.objectives || [];
  const managerScore = evaluation.managerScore || 0;
  const selfScore = evaluation.selfScore || 0;
  const adjustedScore = validationForm.adjustedScore ?? managerScore;
  const performanceLevel = getPerformanceLevel(adjustedScore);
  const perfConfig = PERFORMANCE_LEVELS[performanceLevel];
  const baremeInfo = getBaremeInfo(adjustedScore);
  const canValidate = ['manager_eval_done', 'n2_validation_pending'].includes(evaluation.status);

  return (
    <DashboardLayout title="Validation RH">
      <div className="space-y-6 max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <Button
              variant="ghost"
              className="mb-2 -ml-2"
              onClick={() => navigate('/evaluation-campaigns')}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Retour aux campagnes
            </Button>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <FileText className="h-7 w-7 text-purple-500" />
              Validation RH - Évaluation de {emp.firstName} {emp.lastName}
            </h1>
            <p className="text-slate-500 mt-1">
              Campagne: {evaluation.campaignName || evaluation.campaign?.name || 'N/A'}
            </p>
          </div>
          <Badge className={`${perfConfig.color} border px-4 py-2 text-sm`}>
            <Award className="w-4 h-4 mr-2" />
            {perfConfig.label} ({adjustedScore}/100)
          </Badge>
        </div>

        {/* Employee Info Card */}
        <Card className="border-2 border-purple-100">
          <CardHeader className="pb-3 bg-purple-50/50">
            <CardTitle className="text-lg flex items-center gap-2">
              <User className="h-5 w-5 text-purple-600" />
              Identification de la personne évaluée
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="flex items-start gap-6">
              <Avatar className="h-20 w-20 border-2 border-purple-200">
                <AvatarImage src={emp.photoUrl} />
                <AvatarFallback className="bg-purple-100 text-purple-700 text-xl">
                  {emp.firstName?.[0]}{emp.lastName?.[0]}
                </AvatarFallback>
              </Avatar>
              
              <div className="grid grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-3 flex-1">
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Nom & Prénoms</p>
                  <p className="font-medium text-slate-900">{emp.firstName} {emp.lastName}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Matricule</p>
                  <p className="font-medium text-slate-900">{emp.matricule || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Poste occupé</p>
                  <p className="font-medium text-slate-900">{emp.jobTitle || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Département</p>
                  <p className="font-medium text-slate-900">{evaluation.departmentName || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Date d'embauche</p>
                  <p className="font-medium text-slate-900">
                    {emp.hireDate ? new Date(emp.hireDate).toLocaleDateString('fr-FR') : 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Période évaluée</p>
                  <p className="font-medium text-slate-900">
                    {evaluation.campaign?.periodStart && evaluation.campaign?.periodEnd
                      ? `${new Date(evaluation.campaign.periodStart).toLocaleDateString('fr-FR')} - ${new Date(evaluation.campaign.periodEnd).toLocaleDateString('fr-FR')}`
                      : 'N/A'}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Scores Summary */}
        <div className="grid md:grid-cols-3 gap-4">
          <Card className="border-blue-200 bg-blue-50/30">
            <CardContent className="pt-6 text-center">
              <div className="text-4xl font-bold text-blue-600">{selfScore}</div>
              <p className="text-sm text-blue-700 mt-1">Auto-évaluation</p>
              <p className="text-xs text-slate-500 mt-2">Score de l'employé</p>
            </CardContent>
          </Card>
          
          <Card className="border-amber-200 bg-amber-50/30">
            <CardContent className="pt-6 text-center">
              <div className="text-4xl font-bold text-amber-600">{managerScore}</div>
              <p className="text-sm text-amber-700 mt-1">Évaluation Manager</p>
              <p className="text-xs text-slate-500 mt-2">Score du N+1</p>
            </CardContent>
          </Card>
          
          <Card className={`border-2 ${adjustedScore >= 85 ? 'border-emerald-300 bg-emerald-50/50' : adjustedScore >= 60 ? 'border-blue-300 bg-blue-50/50' : 'border-red-300 bg-red-50/50'}`}>
            <CardContent className="pt-6 text-center">
              <div className={`text-4xl font-bold ${adjustedScore >= 85 ? 'text-emerald-600' : adjustedScore >= 60 ? 'text-blue-600' : 'text-red-600'}`}>
                {adjustedScore}
              </div>
              <p className="text-sm text-slate-700 mt-1">Score Final</p>
              <Badge className={`mt-2 ${perfConfig.color} border`}>{perfConfig.label}</Badge>
            </CardContent>
          </Card>
        </div>

        {/* Objectives Detail */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="h-5 w-5 text-blue-600" />
              Résultats attendus - Détail des objectifs ({objectives.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {objectives.map((obj, idx) => (
              <div key={obj.id || idx} className="border rounded-lg p-4 bg-slate-50">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant="outline" className="text-xs">Objectif {idx + 1}</Badge>
                      <Badge variant="secondary" className="text-xs">Poids: {obj.weight || 20}%</Badge>
                    </div>
                    <h4 className="font-semibold text-slate-900">{obj.title}</h4>
                    {obj.description && (
                      <p className="text-sm text-slate-600 mt-1">{obj.description}</p>
                    )}
                    {obj.indicators && (
                      <p className="text-xs text-slate-500 mt-1">
                        <span className="font-medium">Indicateurs:</span> {obj.indicators}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-4 text-center">
                    <div className="bg-blue-50 rounded-lg px-3 py-2 min-w-[70px]">
                      <div className="text-lg font-bold text-blue-600">{obj.selfScore ?? '-'}</div>
                      <div className="text-xs text-blue-700">Auto-éval</div>
                    </div>
                    <div className="bg-amber-50 rounded-lg px-3 py-2 min-w-[70px]">
                      <div className="text-lg font-bold text-amber-600">{obj.managerScore ?? '-'}</div>
                      <div className="text-xs text-amber-700">Manager</div>
                    </div>
                  </div>
                </div>
                
                {/* Comments */}
                <div className="mt-3 grid md:grid-cols-2 gap-3">
                  {(obj.selfComment || obj.selfAchievements || obj.selfChallenges) && (
                    <div className="bg-blue-50/50 rounded p-2 text-sm space-y-1">
                      <p className="text-xs text-blue-600 font-medium mb-1">Auto-évaluation employé :</p>
                      {obj.selfAchievements && (
                        <p className="text-slate-700 text-xs"><span className="font-medium">Réalisations :</span> <span className="italic">{obj.selfAchievements}</span></p>
                      )}
                      {obj.selfChallenges && (
                        <p className="text-slate-700 text-xs"><span className="font-medium">Défis :</span> <span className="italic">{obj.selfChallenges}</span></p>
                      )}
                      {obj.selfComment && (
                        <p className="text-slate-700 text-xs"><span className="font-medium">Commentaire :</span> <span className="italic">"{obj.selfComment}"</span></p>
                      )}
                    </div>
                  )}
                  {obj.managerComment && (
                    <div className="bg-amber-50/50 rounded p-2 text-sm">
                      <p className="text-xs text-amber-600 font-medium mb-1">Commentaire manager:</p>
                      <p className="text-slate-700 italic">"{obj.managerComment}"</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Manager Evaluation Details */}
        {managerEval && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-amber-600" />
                Appréciation du Manager
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Strengths */}
                {managerEval.strengths?.length > 0 && (
                  <div className="bg-emerald-50 rounded-lg p-4">
                    <h4 className="font-medium text-emerald-800 mb-2 flex items-center gap-2">
                      <TrendingUp className="h-4 w-4" />
                      Points forts
                    </h4>
                    <ul className="space-y-1">
                      {managerEval.strengths.map((s, i) => s && (
                        <li key={`strength-${i}-${s}`} className="text-sm text-emerald-700 flex items-start gap-2">
                          <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
                          {s}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {/* Areas of improvement */}
                {managerEval.areasOfImprovement?.length > 0 && (
                  <div className="bg-amber-50 rounded-lg p-4">
                    <h4 className="font-medium text-amber-800 mb-2 flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4" />
                      Axes d'amélioration
                    </h4>
                    <ul className="space-y-1">
                      {managerEval.areasOfImprovement.map((a, i) => a && (
                        <li key={`area-${i}-${a}`} className="text-sm text-amber-700 flex items-start gap-2">
                          <Target className="h-4 w-4 mt-0.5 flex-shrink-0" />
                          {a}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              
              {/* Global comment */}
              {managerEval.globalComment && (
                <div className="bg-slate-50 rounded-lg p-4">
                  <h4 className="font-medium text-slate-800 mb-2">Commentaire global du manager</h4>
                  <p className="text-slate-700 italic">"{managerEval.globalComment}"</p>
                </div>
              )}
              
              {/* Recommended actions */}
              {managerEval.recommendedActions && (
                <div className="bg-purple-50 rounded-lg p-4">
                  <h4 className="font-medium text-purple-800 mb-2">Actions recommandées</h4>
                  <p className="text-purple-700">{managerEval.recommendedActions}</p>
                </div>
              )}
              
              {/* Engagement & Satisfaction */}
              {(managerEval.engagementLevel || managerEval.satisfactionLevel) && (
                <div className="flex gap-6">
                  {managerEval.engagementLevel && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-slate-600">Niveau d'engagement:</span>
                      <div className="flex">
                        {[1, 2, 3, 4, 5].map(n => (
                          <Star
                            key={n}
                            className={`h-4 w-4 ${n <= managerEval.engagementLevel ? 'text-amber-400 fill-amber-400' : 'text-slate-300'}`}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                  {managerEval.satisfactionLevel && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-slate-600">Niveau de satisfaction:</span>
                      <div className="flex">
                        {[1, 2, 3, 4, 5].map(n => (
                          <Star
                            key={n}
                            className={`h-4 w-4 ${n <= managerEval.satisfactionLevel ? 'text-blue-400 fill-blue-400' : 'text-slate-300'}`}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Barème & Proposition */}
        <Card className="border-2 border-slate-200">
          <CardHeader className="bg-slate-50">
            <CardTitle className="flex items-center gap-2">
              <Award className="h-5 w-5 text-slate-700" />
              Proposition après notation globale
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            <div className={`rounded-lg p-4 mb-4 ${
              adjustedScore >= 85 ? 'bg-emerald-50 border border-emerald-200' :
              adjustedScore >= 75 ? 'bg-green-50 border border-green-200' :
              adjustedScore >= 60 ? 'bg-blue-50 border border-blue-200' :
              adjustedScore >= 50 ? 'bg-amber-50 border border-amber-200' :
              'bg-red-50 border border-red-200'
            }`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Note équivalente</p>
                  <p className="text-xl font-bold">{baremeInfo.note}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-slate-600">Statut</p>
                  <p className="font-semibold">{baremeInfo.status}</p>
                </div>
              </div>
              <Separator className="my-3" />
              <div>
                <p className="text-sm text-slate-600 mb-1">Action proposée:</p>
                <p className="font-medium">{baremeInfo.action}</p>
              </div>
            </div>

            {/* Barème complet */}
            <div className="mt-4">
              <h4 className="text-sm font-medium text-slate-700 mb-2">Barème de notation:</h4>
              <div className="grid grid-cols-5 gap-2 text-xs">
                {BAREME.map((b) => (
                  <div
                    key={b.note}
                    className={`p-2 rounded text-center ${
                      adjustedScore >= b.min && adjustedScore <= b.max
                        ? 'bg-purple-100 border-2 border-purple-400'
                        : 'bg-slate-50 border border-slate-200'
                    }`}
                  >
                    <p className="font-bold">{b.min}-{b.max}</p>
                    <p className="text-slate-600">{b.note}</p>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Validation Form - Only if can validate */}
        {canValidate && (
          <Card className="border-2 border-purple-300 bg-purple-50/30">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-purple-800">
                <CheckCircle2 className="h-5 w-5" />
                Décision de validation RH
              </CardTitle>
              <CardDescription>
                Vous pouvez valider l'évaluation ou la renvoyer au manager pour révision
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Score Adjustment */}
              <div>
                <Label className="text-sm font-medium text-slate-700">
                  Ajuster le score final (optionnel)
                </Label>
                <p className="text-xs text-slate-500 mb-3">
                  Score actuel du manager: {managerScore}/100. Vous pouvez l'ajuster si nécessaire.
                </p>
                <div className="flex items-center gap-4">
                  <Slider
                    value={[validationForm.adjustedScore ?? managerScore]}
                    onValueChange={([val]) => setValidationForm(prev => ({ ...prev, adjustedScore: val }))}
                    max={100}
                    step={1}
                    className="flex-1"
                  />
                  <div className={`text-2xl font-bold px-4 py-2 rounded-lg min-w-[80px] text-center ${
                    adjustedScore >= 85 ? 'bg-emerald-100 text-emerald-700' :
                    adjustedScore >= 60 ? 'bg-blue-100 text-blue-700' :
                    'bg-red-100 text-red-700'
                  }`}>
                    {adjustedScore}
                  </div>
                </div>
              </div>

              {/* Comment */}
              <div>
                <Label className="text-sm font-medium text-slate-700">
                  Commentaire de validation
                </Label>
                <Textarea
                  value={validationForm.comment}
                  onChange={(e) => setValidationForm(prev => ({ ...prev, comment: e.target.value }))}
                  placeholder="Ajoutez votre commentaire de validation (facultatif)..."
                  rows={3}
                  className="mt-2"
                />
              </div>

              {/* Action Buttons */}
              <div className="flex justify-between pt-4 border-t">
                <Button
                  variant="outline"
                  className="border-red-300 text-red-700 hover:bg-red-50"
                  onClick={() => setShowRejectDialog(true)}
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Renvoyer au manager
                </Button>
                
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={handleDownloadPDF}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Télécharger PDF
                  </Button>
                  <Button
                    className="bg-purple-600 hover:bg-purple-700"
                    onClick={() => setShowApproveDialog(true)}
                  >
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    Valider l'évaluation
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Already validated message */}
        {!canValidate && evaluation.status === 'validated' && (
          <Card className="border-2 border-emerald-300 bg-emerald-50/30">
            <CardContent className="pt-6 text-center">
              <CheckCircle2 className="h-12 w-12 mx-auto text-emerald-500 mb-3" />
              <h3 className="text-lg font-semibold text-emerald-800">Évaluation déjà validée</h3>
              <p className="text-emerald-600 mt-1">
                Validée le {evaluation.validatedAt ? new Date(evaluation.validatedAt).toLocaleDateString('fr-FR') : 'N/A'}
              </p>
              <Button variant="outline" className="mt-4" onClick={handleDownloadPDF}>
                <Download className="h-4 w-4 mr-2" />
                Télécharger le rapport PDF
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Approve Dialog */}
        <AlertDialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                Confirmer la validation
              </AlertDialogTitle>
              <AlertDialogDescription>
                Vous êtes sur le point de valider l'évaluation de <strong>{emp.firstName} {emp.lastName}</strong> avec un score final de <strong>{adjustedScore}/100</strong> ({perfConfig.label}).
                <br /><br />
                Cette action finalisera l'évaluation et permettra la génération du rapport officiel.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={submitting}>Annuler</AlertDialogCancel>
              <AlertDialogAction
                className="bg-emerald-600 hover:bg-emerald-700"
                onClick={() => handleValidate(true)}
                disabled={submitting}
              >
                {submitting ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                )}
                Valider
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Reject Dialog */}
        <AlertDialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <RotateCcw className="h-5 w-5 text-amber-500" />
                Renvoyer au manager
              </AlertDialogTitle>
              <AlertDialogDescription>
                L'évaluation sera renvoyée au manager pour révision. Assurez-vous d'ajouter un commentaire expliquant les modifications attendues.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="py-4">
              <Label>Commentaire (raison du renvoi)</Label>
              <Textarea
                value={validationForm.comment}
                onChange={(e) => setValidationForm(prev => ({ ...prev, comment: e.target.value }))}
                placeholder="Expliquez pourquoi l'évaluation doit être révisée..."
                rows={3}
                className="mt-2"
              />
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={submitting}>Annuler</AlertDialogCancel>
              <AlertDialogAction
                className="bg-amber-600 hover:bg-amber-700"
                onClick={() => handleValidate(false)}
                disabled={submitting || !validationForm.comment.trim()}
              >
                {submitting ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <RotateCcw className="h-4 w-4 mr-2" />
                )}
                Renvoyer
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
}
